// src/layout/Topbar.jsx
// 画面上部のヘッダー。ページタイトル / ロールバッジ / ユーザーメニュー / ログアウト / 通知ベル。

import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ROLE_LABEL, ROLE_COLOR } from '../config/roles';
import { getPageLabel } from '../config/menu';
import Icon from '../components/Icon';
import DevVerifyDialog from '../components/DevVerifyDialog';

export default function Topbar({ user, onLogout, onMenuClick = () => {} }) {
  const location = useLocation();
  const title    = getPageLabel(location.pathname, user);
  const initial  = (user.name || user.student_id || '?').charAt(0).toUpperCase();

  // 開発者モード（開発者の印がある人だけ）【2026-09-29】
  //   オン: ほかの開発者のアカウント・出席・授業も表示する（開発者専用の機能も使える）
  //   オフ: 同じ役割の普通の人とまったく同じ画面
  // 通知の未読数（ベルのバッジ）【2026-10-08 レビュー】
  //   画面を移るたび・1分ごと・通知ページで既読にしたときに取り直す。
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () => {
      fetch('/api/notifications?limit=1', { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } })
        .then(r => r.json())
        .then(d => { if (alive && d && d.success) setUnread(Number(d.unread_count) || 0); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    window.addEventListener('notifications-changed', load);
    return () => { alive = false; clearInterval(t); window.removeEventListener('notifications-changed', load); };
  }, [location.pathname]);

  const [devBusy, setDevBusy] = useState(false);
  const [devVerify, setDevVerify] = useState(false);
  const devOn = !!(user.isDeveloper && user.developerMode && !user.devLocked);
  const toggleDevMode = async () => {
    if (devBusy) return;
    // まだメールのコードで確かめていなければ、先に確かめる【2026-10-16】
    if (user.devLocked) { setDevVerify(true); return; }
    setDevBusy(true);
    try {
      const res = await fetch('/api/user/developer-mode', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('token') },
        body: JSON.stringify({ enabled: !devOn }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        // 表示中のデータを見え方に合わせて取り直すため、画面ごと読み込み直す
        window.location.reload();
        return;
      }
      if (data.code === 'DEV_MFA_REQUIRED') { setDevVerify(true); setDevBusy(false); return; }
      alert(data.message || '開発者モードを切り替えられませんでした');
    } catch (e) {
      alert('ネットワークエラーで開発者モードを切り替えられませんでした');
    }
    setDevBusy(false);
  };

  return (
    <>
    <header className="topbar">
      {/* スマホ用メニューボタン（デスクトップではCSSで非表示）【2026-07-21】 */}
      <button className="mobile-menu-btn" aria-label="メニューを開く" onClick={onMenuClick}><Icon name="menu" size={22} /></button>
      <h1 className="topbar-title">{title}</h1>
      <div className="topbar-right">
        <span
          className="topbar-role"
          style={{ background: ROLE_COLOR[user.role] + '22', color: ROLE_COLOR[user.role] }}
        >
          {ROLE_LABEL[user.role]}
        </span>
        {user.isDeveloper && (
          <button
            type="button"
            className={'topbar-devmode' + (devOn ? ' on' : '')}
            onClick={toggleDevMode}
            disabled={devBusy}
            title={user.devLocked ? '開発者の機能を使うには、メールの確認コードで本人確認します'
              : devOn ? '開発者モード：オン（開発者のデータも表示中）。押すとオフ' : '開発者モード：オフ（普通の画面）。押すとオン'}
          >
            開発 {user.devLocked ? '🔒' : devOn ? 'ON' : 'OFF'}
          </button>
        )}
        <Link to="/profile" className="topbar-user" title="アカウント設定を開く">
          <div className="topbar-avatar">{initial}</div>
          <div className="topbar-userinfo">
            <div className="topbar-username">{user.name || user.student_id}</div>
            <div className="topbar-userid">{user.student_id}</div>
          </div>
        </Link>
        <button className="topbar-logout" onClick={onLogout}>ログアウト</button>
        <NavLink
          to="/notifications"
          className={({ isActive }) => 'topbar-icon-btn' + (isActive ? ' active' : '')}
          title={unread > 0 ? `通知を開く（未読 ${unread} 件）` : '通知を開く'}
          style={{ position: 'relative' }}
        >
          <Icon name="bell" size={19} />
          {unread > 0 && (
            <span aria-label={`未読 ${unread} 件`}
                  style={{ position: 'absolute', top: -5, right: -5, minWidth: 18, height: 18, padding: '0 5px',
                           borderRadius: 999, background: '#ef4444', color: '#fff', fontSize: '0.7rem',
                           fontWeight: 700, lineHeight: '18px', textAlign: 'center', boxSizing: 'border-box' }}>
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </NavLink>
      </div>
    </header>
    {devVerify && <DevVerifyDialog onClose={() => setDevVerify(false)} />}
    {devOn && (
      <div className="topbar-devbanner" role="status">
        開発者モード：開発者のアカウント・出席・授業もまとめて表示しています（普通の人の画面には出ません）
      </div>
    )}
    </>
  );
}
